import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Lock, CheckCircle2, ExternalLink, RefreshCw, Loader2, LogOut, ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AriaLogo } from './AriaLogo';
import { Footer } from './Footer';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';
import { toast } from 'sonner';

export const SubscribeGate = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { billing, refresh } = useBilling();
  const [checking, setChecking] = useState(false);

  const link = billing?.payment_link
    ? `${billing.payment_link}?prefilled_email=${encodeURIComponent(user?.email || '')}`
    : null;

  const handleRefresh = async () => {
    setChecking(true);
    try {
      const res = await refresh();
      if (res?.subscribed) {
        toast.success(t('billing.refresh_success'));
      } else {
        toast.error(t('billing.refresh_fail'));
      }
    } catch (e) {
      toast.error(t('billing.refresh_fail'));
    } finally {
      setChecking(false);
    }
  };

  const features = ['feature_1', 'feature_2', 'feature_3', 'feature_4'];

  return (
    <div className="min-h-screen bg-[hsl(210,25%,6%)] grain-overlay flex flex-col" data-testid="subscribe-gate">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          <div className="flex flex-col items-center text-center mb-6">
            <AriaLogo size={56} className="mb-4 drop-shadow-lg" />
            <div className="w-10 h-10 rounded-full bg-amber-500/15 ring-1 ring-amber-500/30 flex items-center justify-center mb-3">
              <Lock size={18} className="text-amber-300" />
            </div>
            <h1 className="text-xl font-bold text-foreground" data-testid="gate-title">
              {t('billing.gate_title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('billing.gate_subtitle')}</p>
          </div>

          <Card className="bg-[hsl(210,18%,9%)] border-teal-500/25">
            <CardContent className="py-6 space-y-5">
              <div className="text-center">
                <p className="text-lg font-bold text-foreground">{t('billing.gate_plan')}</p>
                <p className="text-xs text-teal-300 mt-0.5">{t('billing.gate_trial_note')}</p>
              </div>

              <ul className="space-y-2.5">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/85">
                    <CheckCircle2 size={16} className="text-teal-400 shrink-0 mt-0.5" />
                    {t(`billing.${f}`)}
                  </li>
                ))}
              </ul>

              <div className="space-y-2.5">
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer" className="block" data-testid="gate-subscribe-link">
                    <Button className="w-full h-11 gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)] font-semibold" data-testid="gate-subscribe-button">
                      <ExternalLink size={16} />
                      {t('billing.subscribe_stripe')}
                    </Button>
                  </a>
                )}
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={checking}
                  className="w-full h-10 gap-2 text-sm border-teal-500/40 text-teal-300 hover:bg-teal-500/10 hover:text-teal-200"
                  data-testid="gate-refresh-button"
                >
                  {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                  {checking ? t('billing.checking') : t('billing.already_paid')}
                </Button>
              </div>

              <p className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="gate-email-note">
                {t('billing.email_note', { email: user?.email || '' })}
              </p>

              <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground">
                <ShieldCheck size={12} className="text-teal-400" />
                {t('billing.secure_note')}
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-4">
            <button
              onClick={logout}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors duration-150"
              data-testid="gate-logout-button"
            >
              <LogOut size={12} />
              {t('billing.logout')}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SubscribeGate;
